import React, {useState, useEffect, useContext} from "react";
import {Row} from "antd";
import CircularLoader from "@common/CircularLoader";
import Alert from "@common/Alert";
import AdminIdTag, {ADMIN_ID_TAG_TYPES} from "@common/AdminIdTag";
import DragAndDropQuestion, {DragAndDropQuestionResultsSwitch} from "./questionTypes/DragAndDropQuestion";
import GenericQuestion from "./questionTypes/GenericQuestion";
import MathJaxHTMLContent from "@common/MathJaxHTMLContent";
import {ContentContext} from "@context/ContentContext";

export const Question = props => {
    const {
        question,
        className,
        isLoading = true,
        result = null,
        dndResultView,
        displayAnswerOnResult = false,
        readOnly = false,
        updateQuestionData,
        resultFormat,
        onLoad,
    } = props;
    const [error, setError] = useState();
    const [showResult, setShowResult] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [subComponentLoading, setSubComponentLoading] = useState(true);
    const {content} = useContext(ContentContext);

    useEffect(() => {
        setSubComponentLoading(true);
        setError(null);
    }, [question?.questionId]);

    useEffect(() => {
        if (!isLoading && !question) setError("Question not found");
    }, [question, isLoading]);

    useEffect(() => {
        if (!result) {
            setShowResult(false);
            setIsReadOnly(readOnly);
            return;
        }

        const attemptsExhausted =
            result.attemptCount &&
            result.maxAttempts &&
            result.attemptCount > (result.maxAttempts + 1);
        const shouldShowAnswer =
            !result.maxAttempts ||
            !result.attemptCount ||
            attemptsExhausted;
        setShowResult(shouldShowAnswer);

        if (readOnly || result) {
            setIsReadOnly(true);
        } else {
            setIsReadOnly(false);
        }

    }, [result, readOnly, question?.questionId]);

    const handleLoad = (value) => {
        setSubComponentLoading(false);
        onLoad?.(value);
    }

    const handleError = error => {
        setError(error);
    }

    const handleUpdateQuestionData = (questionData, isSubmittable) => {
        if (typeof updateQuestionData !== "function") return;

        let finalDataString = "";
        if (resultFormat === "json") {
            const requestMap = {};
            questionData.forEach(item => {
                const val = Array.isArray(item.value)
                    ? item.value.map(String)
                    : [String(item.value)];
                if (requestMap[item.name]) {
                    requestMap[item.name] = [...requestMap[item.name], ...val];
                } else {
                    requestMap[item.name] = val;
                }
            });
            finalDataString = JSON.stringify(requestMap);
        } else {
            const params = new URLSearchParams();
            questionData.forEach(item => {
                if (Array.isArray(item.value)) {
                    item.value.forEach(v => params.append(item.name, v));
                } else {
                    params.append(item.name, item.value);
                }
            });
            finalDataString = params.toString();
        }
        updateQuestionData?.(finalDataString, isSubmittable);
    }

    const universalQuestionProps = {
        question,
        result: showResult ? result : null,
        displayAnswerOnResult,
        readOnly: isReadOnly,
        onLoad: handleLoad,
        onError: handleError,
        updateQuestionData: handleUpdateQuestionData,
    }
    const loading = isLoading || subComponentLoading;

    return (
        <section
            className={[
                'ch-question',
                className,
                content.currentSyllabus?.syllabusContentClass
            ].filter(Boolean).join(' ')}
            data-question-id={question?.questionId}
            aria-busy={loading}
            aria-live="polite"
        >
            {question && (
                <AdminIdTag
                    id={question.questionId}
                    type={ADMIN_ID_TAG_TYPES.QUESTION}
                />
            )}
            {error ? (
                <Alert type="error" title={error} role="alert" />
            ) : (
                <>
                    {loading && (
                        <Row>
                            <CircularLoader displayInElement={true} />
                        </Row>
                    )}
                    {question && (
                        <div style={{display: loading ? 'none' : 'block'}}>
                            {question.questionAnswerType === "DRAG_DROP" && (
                                <DragAndDropQuestion
                                    {...universalQuestionProps}
                                    resultsView={dndResultView}
                                />
                            )}
                            {question.questionAnswerType !== "DRAG_DROP" && (
                                <GenericQuestion
                                    {...universalQuestionProps}
                                />
                            )}
                            {question.questionType === "WALKTHROUGH" && (
                                <GenericQuestion
                                    {...universalQuestionProps}
                                />
                            )}
                        </div>
                    )}
                </>
            )}
        </section>
    );
}

export const QuestionAnswerResultBlock = props => {
    const {result, questionAnswerType, dndValue, dndOnChange, isWalkthrough = false, inDialog = false, ...other} = props;

    if (!result) return <></>;

    const title = result.skipped ? "Not attempted" : result?.result == true ? "Correct" : "Incorrect";
    const markText = result.maxMark > 1 ? " (Score " + result.mark + "/" + result.maxMark + ")" : "";
    const header = title + markText;

    let feedback = result?.correctAnswerFeedback;
    if (isWalkthrough) {
        feedback = result.feedback;
    } else if (questionAnswerType === "DRAG_DROP") {
        feedback = feedback.replace(/<div class="genBtn-blk">[\s\S]*?<\/div>\s*<\/div>/g, "").trim();
    }

    return (
        <div {...other}>
            {!inDialog && (
                <h5 className="margin-t--l">{header}</h5>
            )}
            {result?.result != true && (
                <MathJaxHTMLContent
                    showLoader={false}
                    html={feedback}
                />
            )}
            {result?.result != true && questionAnswerType === "DRAG_DROP" && (
                <DragAndDropQuestionResultsSwitch
                    value={dndValue}
                    onChange={e => dndOnChange(e.target.value)}
                />
            )}
        </div>
    )
}

export default Question;